import { useState } from 'react'
import { Award, Pencil, Plus } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, PermissionGate,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useCompetencies } from '@/features/performance'
import { useDocumentTypes } from '@/features/document'
import { toApiProblem } from '@/lib/api/client'
import { useTrainingCatalogueMutations, useTrainingCourses } from '../api'
import { DELIVERY_LABELS } from '../types'
import type { DeliveryMode } from '../types'
import { Select } from '@/components/ui/select'

/**
 * The course catalogue.
 *
 * Each course states plainly what certificate it will and will not issue. A course with no
 * document type set issues nothing, and saying so here is the difference between a
 * configuration somebody fixes and a silence somebody discovers when a certification lapses.
 */
export function TrainingCataloguePage() {
  useBreadcrumb()
  const courses = useTrainingCourses()
  const competencies = useCompetencies()
  const documentTypes = useDocumentTypes()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { saveCourse, retireCourse } = useTrainingCatalogueMutations()

  const [draft, setDraft] = useState<{
    id?: string
    code: string; title: string; deliveryMode: DeliveryMode; provider: string
    competencyId: string; durationHours: string; certificateDocumentTypeId: string
    certificateValidMonths: string; minimumAttendancePercent: string
  } | null>(null)
  const [retiring, setRetiring] = useState<{ id: string; title: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Course catalogue"
        subtitle="What can be learned, and what a certificate from it means."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                code: '', title: '', deliveryMode: 'INTERNAL', provider: '',
                competencyId: '', durationHours: '', certificateDocumentTypeId: '',
                certificateValidMonths: '', minimumAttendancePercent: '',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New course
          </Button>
        }
      />

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Award className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A certificate is written into the person's document file, so it appears in the
        expiry-alert report alongside their passport and visa. A course with no document type
        set issues none — a certification held only in a training table is one nobody sees
        lapse.
      </p>

      <Card>
        {courses.isPending && <Skeleton className="m-4 h-24" />}
        {courses.isError && <ErrorState error={courses.error} onRetry={() => void courses.refetch()} />}
        {courses.isSuccess && courses.data.length === 0 && (
          <EmptyState
            title="No course is defined"
            description="Nothing can be scheduled until one exists. A course says who provides it, what it certifies, and how much of it somebody has to attend."
          />
        )}
        {courses.isSuccess && courses.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Title</th>
                  <th className="p-3 text-start font-medium">Delivery</th>
                  <th className="p-3 text-start font-medium">Certificate</th>
                  <th className="p-3 text-end font-medium">Min. attendance</th>
                  <th className="p-3 text-end font-medium">Sessions</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {courses.data.map((course) => (
                  <tr key={course.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">{course.code}</td>
                    <td className="p-3">
                      {course.title}
                      {course.provider && (
                        <span className="block text-xs text-slate-600">{course.provider}</span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      {DELIVERY_LABELS[course.deliveryMode as DeliveryMode]
                        ?? course.deliveryMode}
                    </td>
                    <td className="p-3 max-w-md text-xs text-slate-600">
                      {course.certificateNote}
                    </td>
                    <td className="p-3 text-end text-slate-600">
                      {course.minimumAttendancePercent == null
                        ? 'Not set' : `${course.minimumAttendancePercent}%`}
                    </td>
                    <td className="p-3 text-end">{course.sessionCount}</td>
                    <td className="p-3 text-end">
                      <PermissionGate permission="training:configure">
                        <Button
                          type="button" variant="ghost" size="sm"
                          aria-label={`Edit ${course.title}`}
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: course.id,
                              code: course.code,
                              title: course.title,
                              deliveryMode: course.deliveryMode as DeliveryMode,
                              provider: course.provider ?? '',
                              competencyId: course.competencyId ?? '',
                              durationHours: course.durationHours == null
                                ? '' : String(course.durationHours),
                              certificateDocumentTypeId:
                                course.certificateDocumentTypeId ?? '',
                              certificateValidMonths: course.certificateValidMonths == null
                                ? '' : String(course.certificateValidMonths),
                              minimumAttendancePercent:
                                course.minimumAttendancePercent == null
                                  ? '' : String(course.minimumAttendancePercent),
                            })
                          }}
                        >
                          <Pencil className="size-4" aria-hidden="true" />
                        </Button>
                        {course.active && (
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => {
                              setError(null)
                              setRetiring({ id: course.id, title: course.title })
                            }}
                          >
                            Retire
                          </Button>
                        )}
                      </PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={retiring !== null}
        onOpenChange={(open) => { if (!open) setRetiring(null) }}
        title={`Retire ${retiring?.title ?? 'this course'}?`}
        description="It stops being offered for new sessions. Everybody's training record keeps naming it, and a certificate issued against it keeps resolving to what it certified."
        confirmLabel="Retire it"
        loading={retireCourse.isPending}
        error={error}
        onConfirm={() => {
          if (!retiring) return
          setError(null)
          retireCourse.mutate(retiring.id, {
            onSuccess: () => setRetiring(null),
            onError: (caught) => setError(
              toApiProblem(caught)?.detail ?? 'That course could not be retired.'),
          })
        }}
      />

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        size="lg"
        title={draft?.id ? 'Edit course' : 'New course'}
        description="Leave the certificate fields blank for a course that issues none."
        submitLabel="Save course"
        loading={saveCourse.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              await saveCourse.mutateAsync({
                id: draft.id,
                legalEntityId: entities.data?.[0]?.id ?? null,
                code: draft.code,
                title: draft.title,
                deliveryMode: draft.deliveryMode,
                provider: draft.provider || null,
                competencyId: draft.competencyId || null,
                durationHours: draft.durationHours ? Number(draft.durationHours) : null,
                certificateDocumentTypeId: draft.certificateDocumentTypeId || null,
                certificateValidMonths: draft.certificateValidMonths
                  ? Number(draft.certificateValidMonths) : null,
                minimumAttendancePercent: draft.minimumAttendancePercent
                  ? Number(draft.minimumAttendancePercent) : null,
              })
              setDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That course could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="course-code">Code</Label>
              <Input
                id="course-code" value={draft.code}
                onChange={(event) =>
                  setDraft({ ...draft, code: event.target.value.toUpperCase() })}
              />
            </div>
            <div>
              <Label htmlFor="course-title">Title</Label>
              <Input
                id="course-title" value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="course-delivery">Delivery</Label>
              <Select
                id="course-delivery"
                className="w-full"
                value={draft.deliveryMode}
                onChange={(event) =>
                  setDraft({ ...draft, deliveryMode: event.target.value as DeliveryMode })}
              >
                {Object.entries(DELIVERY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="course-provider">Provider</Label>
              <Input
                id="course-provider" value={draft.provider}
                onChange={(event) => setDraft({ ...draft, provider: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="course-competency">Addresses which competency</Label>
              <Select
                id="course-competency"
                className="w-full"
                value={draft.competencyId}
                onChange={(event) => setDraft({ ...draft, competencyId: event.target.value })}
              >
                <option value="">Not mapped to one</option>
                {competencies.data?.map((competency) => (
                  <option key={competency.id} value={competency.id}>{competency.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="course-hours">Duration, hours</Label>
              <Input
                id="course-hours" type="number" value={draft.durationHours}
                onChange={(event) => setDraft({ ...draft, durationHours: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="course-doctype">Certificate goes to</Label>
              <Select
                id="course-doctype"
                className="w-full"
                value={draft.certificateDocumentTypeId}
                onChange={(event) =>
                  setDraft({ ...draft, certificateDocumentTypeId: event.target.value })}
              >
                <option value="">No certificate is issued</option>
                {documentTypes.data?.map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                The document type it is filed under, so it shows up in expiry alerts.
              </p>
            </div>
            <div>
              <Label htmlFor="course-validity">Certificate valid for, months</Label>
              <Input
                id="course-validity" type="number" value={draft.certificateValidMonths}
                onChange={(event) =>
                  setDraft({ ...draft, certificateValidMonths: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Blank means it does not expire — a claim, not a default.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="course-attendance">Minimum attendance, %</Label>
              <Input
                id="course-attendance" type="number"
                value={draft.minimumAttendancePercent}
                onChange={(event) =>
                  setDraft({ ...draft, minimumAttendancePercent: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                No default: what counts as having done the course is your judgement, and a
                number invented here would deny somebody a certificate they had earned.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
